/** 新增分包时复用公司库资料，避免手填同名公司后无法关联项目或绑定到错误账号。
 * 只读取当前用户有权访问的有效公司；本工种优先展示，其他工种仍可搜索选择，兼容一家公司承接多工种。
 * 选择只回传公司身份，不开户、不发送通知；真实账号关联由创建合同接口完成。
 */
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import useSubcontractorRefresh from '../../hooks/useSubcontractorRefresh';

/** value 为显式选中的公司，onChange 回传公司或 null（切回手填）；刷新候选不覆盖用户表单。
 * 财务表单可通过 displayValue 展示实际支票收款名或历史名称，并关闭手填入口；新增分包仍沿用默认行为。
 * label/placeholder/emptyMessage 让不同业务复用一个选择区，selectedHint=false 可隐藏不适用的手填提示。
 */
export function SubcontractCompanyPicker({ api, tradeKey, value, onChange, disabled, zh, portalContainer, selectedHint, label, displayValue, placeholder, emptyMessage, allowManual = true }) {
  const labelId = useId();
  const [open, setOpen] = useState(false), [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true), [error, setError] = useState(false);
  const sequence = useRef(0);
  const load = useCallback(async () => {
    const request = ++sequence.current;
    setLoading(true);
    try {
      const { data } = await api.get('/subcontractor-companies');
      if (request !== sequence.current) return;
      setCompanies(data.filter(c => c.id && c.name?.trim() && c.active !== false));
      setError(false);
    } catch {
      if (request === sequence.current) setError(true);
    } finally {
      if (request === sequence.current) setLoading(false);
    }
  }, [api]);
  useEffect(() => { load(); return () => { sequence.current += 1; }; }, [load]);
  useSubcontractorRefresh(load, { enabled: !disabled });
  const groups = [
    [zh ? '本工种分包商' : 'Matching trade', companies.filter(c => c.trade === tradeKey)],
    [zh ? '其他已登记公司' : 'Other registered companies', companies.filter(c => c.trade !== tradeKey)],
  ];
  const choose = company => { onChange(company); setOpen(false); };
  return <div className="space-y-1 text-sm">
    <span id={labelId}>{label ?? (zh ? '从分包商库选择' : 'Choose from subcontractor directory')}</span>
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" aria-expanded={open} aria-labelledby={labelId} disabled={disabled} data-testid="sub-new-company" className="w-full justify-between gap-2 font-normal">
          <span className="min-w-0 truncate">{(displayValue ?? value?.name) || placeholder || (zh ? '搜索已登记分包商' : 'Search registered subcontractors')}</span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      {/* 新增分包弹窗会将候选层放进对话框自身，避免外置 Portal 被对话框滚动锁拦截鼠标滚轮。 */}
      <PopoverContent portalContainer={portalContainer} align="start" className="w-[var(--radix-popover-trigger-width)] max-w-[calc(100vw-2rem)] p-0">
        <Command>
          <CommandInput aria-label={zh ? '搜索分包商名称或工种' : 'Search company or trade'} placeholder={zh ? '搜索名称、工种…' : 'Search company, trade…'} data-testid="sub-new-company-search" />
          {loading && <div role="status" className="flex items-center gap-2 p-3 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />{zh ? '正在加载分包商库…' : 'Loading directory…'}</div>}
          {error && <div role="alert" className="flex flex-wrap items-center justify-between gap-2 p-3"><span className="text-destructive">{zh ? '分包商库加载失败' : 'Unable to load directory'}</span><Button type="button" variant="outline" size="sm" disabled={loading} onClick={load} data-testid="sub-new-company-retry">{zh ? '重试' : 'Retry'}</Button></div>}
          <CommandList className="max-h-52" aria-label={zh ? '已登记分包商' : 'Registered subcontractors'}>
            {!loading && !error && <CommandEmpty>{emptyMessage || (allowManual ? (zh ? '没有匹配的公司，可返回手动填写名称。' : 'No matching company. You can enter a name manually.') : (zh ? '没有匹配的公司。' : 'No matching company.'))}</CommandEmpty>}
            {!error && groups.filter(([, rows]) => rows.length).map(([heading, rows]) => <CommandGroup key={heading} heading={heading}>
              {rows.map(company => <CommandItem key={company.id} value={company.id} keywords={[company.name, company.legalName || '', company.tradeName || '', company.trade || '']} onSelect={() => choose(company)} disabled={disabled || loading} data-testid={`sub-new-company-${company.id}`} className="items-start py-2">
                <Check aria-hidden="true" className={`mt-0.5 ${value?.id === company.id ? 'opacity-100' : 'opacity-0'}`} />
                <span className="min-w-0"><span className="block break-words">{company.name}</span><span className="block text-xs text-muted-foreground">{[company.tradeName || company.trade, company.region].filter(Boolean).join(' · ')}</span></span>
              </CommandItem>)}
            </CommandGroup>)}
          </CommandList>
        </Command>
        {allowManual && <div className="border-t border-border p-1"><Button type="button" variant="ghost" disabled={disabled} className="w-full justify-start font-normal" onClick={() => choose(null)} data-testid="sub-new-company-manual">{zh ? '手动填写分包商名称' : 'Enter subcontractor name manually'}</Button></div>}
      </PopoverContent>
    </Popover>
    {/* 只有仍允许独立编辑名称的表单才显示手填提示；财务选择结果直接作为收款方展示。 */}
    {value && selectedHint !== false && <p className="text-xs text-muted-foreground">{selectedHint || (zh ? '已选择库内公司；修改名称将切换为手动填写。' : 'Directory company selected. Editing the name switches to manual entry.')}</p>}
  </div>;
}
